import { createReducer, on } from "@ngrx/store";
import * as CompanyActions from "./companies.actions";
import { initialCompaniesState } from "./companies.state";

export const companiesReducer = createReducer(
  initialCompaniesState,

  // Load
  on(CompanyActions.loadCompanies, (state) => ({ ...state, loading: true })),

  on(CompanyActions.loadCompaniesSuccess, (state, { companies }) => ({
    ...state,
    // Deep copy companyGroup objects to avoid shared references
    companies: companies.map(company => ({
      ...company,
      companyGroup: company.companyGroup ? { ...company.companyGroup } : undefined
    })),
    loading: false,
  })),

  on(CompanyActions.loadCompaniesFailure, (state, { error }) => ({
    ...state,
    error,
    loading: false,
  })),

  // Paginated Load
  on(CompanyActions.loadCompaniesPaginated, (state) => ({
    ...state,
    loading: true,
    error: null,
  })),

  on(CompanyActions.loadCompaniesPaginatedSuccess, (state, { items, total }) => ({
    ...state,
    // Deep copy companyGroup objects to avoid shared references
    companies: items.map(company => ({
      ...company,
      companyGroup: company.companyGroup ? { ...company.companyGroup } : undefined
    })),
    total,
    loading: false,
    error: null,
  })),

  // Create
  // Note: We don't immediately add to state here - let the reload handle it
  // This ensures the list is properly sorted and paginated
  on(CompanyActions.createCompaniesuccess, (state) => ({
    ...state,
    // Keep state as is - reload will update with proper sort order
  })),

  on(CompanyActions.createCompanyFailure, (state, { error }) => ({
    ...state,
    error,
  })),

  // ✅ Update - patch company in place so row position doesn't change (active/inactive toggle)
  on(CompanyActions.updateCompanySuccess, (state, { company }) => ({
    ...state,
    companies: state.companies.map((c) =>
      c.id === company.id ? { ...c, ...company, companyGroup: company.companyGroup ?? c.companyGroup } : c
    ),
    searchResultsByRow: Object.fromEntries(
      Object.entries(state.searchResultsByRow).map(([rowIndex, companies]) => [
        rowIndex,
        companies.map((c) =>
          c.id === company.id ? { ...c, ...company, companyGroup: company.companyGroup ?? c.companyGroup } : c
        ),
      ])
    ),
  })),

  on(CompanyActions.updateCompanyFailure, (state, { error }) => ({
    ...state,
    error,
  })),

  // ✅ Delete
  on(CompanyActions.deleteCompanySuccess, (state, { id }) => ({
    ...state,
    companies: state.companies.filter((c) => c.id !== id),
  })),

  on(CompanyActions.deleteCompanyFailure, (state, { error }) => ({
    ...state,
    error,
  })),

  // 🌐 Global Search Companies
  on(CompanyActions.searchCompanies, (state) => ({
    ...state,
    loading: true,
    error: null,
  })),

  on(
    CompanyActions.searchCompaniesSuccess,
    (state, { companies, total, rowIndex }) => ({
      ...state,
      loading: false,
      searchResultsByRow: {
        ...state.searchResultsByRow,
        [rowIndex]: companies,
      },
      total,
    })
  ),

  on(CompanyActions.searchCompaniesFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error,
  })),

  on(CompanyActions.clearCompaniesSearch, (state) => ({
    ...state,
    searchResultsByRow: {},
    total: 0,
  })),
  // 🔍 Manufacturer Autocomplete
  on(CompanyActions.searchManufacturers, (state) => ({
    ...state,
    loading: true,
    error: null,
  })),

  on(CompanyActions.searchManufacturersSuccess, (state, { manufacturers }) => ({
    ...state,
    loading: false,
    manufacturerResults: manufacturers,
  })),

  on(CompanyActions.searchManufacturersFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error,
  })),

  // ✅ Short Name Availability Check
  on(CompanyActions.checkShortNameAvailability, (state) => ({
    ...state,
    shortNameAvailable: null,
    shortNameChecking: true,
    error: null,
  })),

  on(
    CompanyActions.checkShortNameAvailabilitySuccess,
    (state, { available }) => ({
      ...state,
      shortNameAvailable: available,
      shortNameChecking: false,
    })
  ),

  on(CompanyActions.checkShortNameAvailabilityFailure, (state, { error }) => ({
    ...state,
    shortNameAvailable: null,
    shortNameChecking: false,
    error,
  })),

  // ✅ Company Name Availability Check
  on(CompanyActions.checkCompanyNameAvailability, (state) => ({
    ...state,
    companyNameAvailable: null,
    companyNameChecking: true,
    error: null,
  })),

  on(
    CompanyActions.checkCompanyNameAvailabilitySuccess,
    (state, { exists }) => ({
      ...state,
      companyNameAvailable: exists,
      companyNameChecking: false,
    })
  ),

  on(
    CompanyActions.checkCompanyNameAvailabilityFailure,
    (state, { error }) => ({
      ...state,
      companyNameAvailable: null,
      companyNameChecking: false,
      error,
    })
  ),

  // ✅ Alter Code Availability Check
  on(CompanyActions.checkAlterCodeAvailability, (state) => ({
    ...state,
    alterCodeAvailable: null,
    alterCodeChecking: true,
    error: null,
  })),

  on(
    CompanyActions.checkAlterCodeAvailabilitySuccess,
    (state, { available }) => ({
      ...state,
      alterCodeAvailable: available,
      alterCodeChecking: false,
    })
  ),

  on(CompanyActions.checkAlterCodeAvailabilityFailure, (state, { error }) => ({
    ...state,
    alterCodeAvailable: null,
    alterCodeChecking: false,
    error,
  }))
);
